import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  format,
  startOfYear,
  endOfYear,
  isSameYear,
  getQuarter,
  startOfQuarter,
  endOfQuarter,
  parseISO,
  setQuarter,
  addQuarters
} from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import Skeleton from './Skeleton';
import { Calendar, ArrowDownRight, PieChart, TrendingUp } from 'lucide-react';

interface QuarterTotal {
  cash: number;
  card: number;
  invoice: number;
  total: number;
}

export default function QuartersOverview() {
  const navigate = useNavigate();
  const [quarterTotals, setQuarterTotals] = useState<Record<number, QuarterTotal>>({});
  const [loading, setLoading] = useState(true);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [activeYears, setActiveYears] = useState<number[]>([]);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [currentQuarterStats, setCurrentQuarterStats] = useState<QuarterTotal>({ cash: 0, card: 0, invoice: 0, total: 0 });
  const [showTips, setShowTips] = useState(false);

  useEffect(() => {
    fetchActiveYears();
  }, []);

  useEffect(() => {
    if (activeYears.length > 0) {
      fetchQuarterTotals();
    }
  }, [currentYear, activeYears, showTips]);

  const fetchActiveYears = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: settings } = await supabase
        .from('settings')
        .select('active_years, show_tips')
        .eq('user_id', user.id)
        .maybeSingle();

      if (settings) {
        if (settings.active_years) {
          const years = settings.active_years.sort((a, b) => a - b);
          setActiveYears(years);
        }
        setShowTips(settings.show_tips ?? true);
      }
    } catch (error) {
      console.error('Error fetching active years:', error);
    }
  };

  const fetchQuarterTotals = async () => {
    setLoading(true);
    const yearStart = startOfYear(new Date(currentYear, 0, 1));
    const yearEnd = endOfYear(new Date(currentYear, 11, 31));

    try {
      const { data, error } = await supabase
        .from('revenue_entries')
        .select('*')
        .gte('date', format(yearStart, 'yyyy-MM-dd'))
        .lte('date', format(yearEnd, 'yyyy-MM-dd'));

      if (error) throw error;

      // Fetch tips if show_tips is enabled
      let tipsMap: Record<string, number> = {};
      if (showTips) {
        const { data: tipsData } = await supabase
          .from('hidden_revenue')
          .select('date, amount')
          .gte('date', format(yearStart, 'yyyy-MM-dd'))
          .lte('date', format(yearEnd, 'yyyy-MM-dd'));

        if (tipsData) {
          tipsData.forEach(tip => {
            if (!tipsMap[tip.date]) {
              tipsMap[tip.date] = 0;
            }
            tipsMap[tip.date] += tip.amount;
          });
        }
      }

      const totals: Record<number, QuarterTotal> = {
        1: { cash: 0, card: 0, invoice: 0, total: 0 },
        2: { cash: 0, card: 0, invoice: 0, total: 0 },
        3: { cash: 0, card: 0, invoice: 0, total: 0 },
        4: { cash: 0, card: 0, invoice: 0, total: 0 }
      };

      if (data) {
        data.forEach(entry => {
          const entryDate = parseISO(entry.date);
          const quarter = getQuarter(entryDate);
          const tipsAmount = showTips ? (tipsMap[entry.date] || 0) : 0;

          totals[quarter].cash += entry.cash_revenue;
          totals[quarter].card += entry.card_revenue;
          totals[quarter].invoice += entry.invoice_revenue;
          totals[quarter].total += entry.cash_revenue + entry.card_revenue + entry.invoice_revenue + tipsAmount;
        });
      }

      setQuarterTotals(totals);
      
      // Set current quarter stats
      const currentDate = new Date();
      const currentQuarter = getQuarter(currentDate);
      if (currentYear === currentDate.getFullYear()) {
        setCurrentQuarterStats(totals[currentQuarter]);
      } else {
        setCurrentQuarterStats({ cash: 0, card: 0, invoice: 0, total: 0 });
      }
    } catch (error) {
      console.error('Error fetching quarter totals:', error);
    } finally {
      setLoading(false);
    }
  };

  const yearTotals = Object.values(quarterTotals).reduce(
    (acc, quarter) => {
      acc.cash += quarter.cash;
      acc.card += quarter.card;
      acc.invoice += quarter.invoice;
      acc.total += quarter.total;
      return acc;
    },
    { cash: 0, card: 0, invoice: 0, total: 0 }
  );

  const handleYearChange = (year: number) => {
    setCurrentYear(year);
    setYearDropdownOpen(false);
  };

  const handleQuarterClick = (year: number, quarterNumber: number) => {
    navigate(`/quarter/${year}/${quarterNumber}`);
  };

  const currentDate = new Date();
  const currentQuarter = getQuarter(currentDate);
  const isCurrentYear = currentYear === currentDate.getFullYear();
  const currentQuarterText = isCurrentYear 
    ? `Kwartaal ${currentQuarter}` 
    : "Huidig kwartaal";

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-8 w-32" />
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <Skeleton className="h-5 w-32 mb-2" />
              <Skeleton className="h-8 w-24 mb-1" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>

        {/* Table Skeleton */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {[...Array(6)].map((_, i) => (
                    <th key={i} className="px-6 py-3">
                      <Skeleton className="h-4 w-24" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...Array(4)].map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    {[...Array(6)].map((_, colIndex) => (
                      <td key={colIndex} className="px-6 py-4">
                        <Skeleton className="h-6 w-full" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Year Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Kwartaaloverzicht {currentYear}
        </h1>
        
        <div className="relative">
          <button
            onClick={() => setYearDropdownOpen(!yearDropdownOpen)}
            className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <span>Jaar: {currentYear}</span>
            <ArrowDownRight className={`h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform ${yearDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {yearDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
              <div className="py-1">
                {activeYears.map(year => (
                  <button
                    key={year}
                    onClick={() => handleYearChange(year)}
                    className={`block w-full text-left px-4 py-2 text-sm ${
                      year === currentYear
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Current Quarter Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {currentQuarterText}
            </h2>
          </div>
          <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
            €{currentQuarterStats.total.toFixed(2)}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Totale omzet
          </p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-green-600 dark:text-green-400" />
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Contant
            </h2>
          </div>
          <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
            €{currentQuarterStats.cash.toFixed(2)}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {((currentQuarterStats.cash / currentQuarterStats.total) * 100 || 0).toFixed(1)}% van totaal
          </p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Pin
            </h2>
          </div>
          <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
            €{currentQuarterStats.card.toFixed(2)}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {((currentQuarterStats.card / currentQuarterStats.total) * 100 || 0).toFixed(1)}% van totaal
          </p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Rekening
            </h2>
          </div>
          <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
            €{currentQuarterStats.invoice.toFixed(2)}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {((currentQuarterStats.invoice / currentQuarterStats.total) * 100 || 0).toFixed(1)}% van totaal
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/30">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Kwartaal
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Contant
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Pin
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Rekening
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Totaal
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {Object.entries(quarterTotals).map(([quarterKey, totals]) => {
                const quarterNumber = parseInt(quarterKey);
                const isCurrentQuarter = isCurrentYear && quarterNumber === currentQuarter;
                
                return (
                  <tr 
                    key={quarterKey} 
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${
                      isCurrentQuarter ? 'bg-green-50 dark:bg-green-900/10' : ''
                    }`}
                    onClick={() => handleQuarterClick(currentYear, quarterNumber)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <PieChart className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
                        <span className={`text-sm font-medium ${
                          isCurrentQuarter 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-gray-900 dark:text-gray-100'
                        }`}>
                          Kwartaal {quarterKey}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                      €{totals.cash.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                      €{totals.card.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                      €{totals.invoice.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-600 dark:text-blue-400">
                      €{totals.total.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-blue-50/50 dark:bg-blue-900/10">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-800 dark:text-blue-200">
                  Jaartotalen
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{yearTotals.cash.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{yearTotals.card.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{yearTotals.invoice.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-blue-800 dark:text-blue-200">
                  €{yearTotals.total.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}